import "./Ticket_Booking.css";
import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import PaymentPopup from "./PaymentPopup";
import PaymentReceipt from "./PaymentReceipt";
import "./Navbar.js"
import Navbar from "./Navbar.js";
import "./Footer.jsx";
import Footer from "./Footer.jsx";

// API Base URL
const API_BASE_URL = 'https://royaleheritagefiesta.com/royaleheritagefiesta/api';

function Ticket_Booking() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Booking popup state
  const [showPopup, setShowPopup] = useState(false);
  const [selectedSeat, setSelectedSeat] = useState(null);
  
  // Store booking data before payment
  const pendingBookingRef = useRef(null);
  
  // Payment related state
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  
  // Standing ticket state
  const [ticketQuantity, setTicketQuantity] = useState(1);
  
  // Customer details
  const [customer, setCustomer] = useState({
    name: "",
    phone: "",
    email: ""
  });

  // Set OTP as always verified (no OTP check)
  const otpVerified = true;
  
  const location = useLocation();
  const eventId = location.state?.eventId;
  const [event, setEvent] = useState(null);
  const [bookedSeats, setBookedSeats] = useState([]);
  const [ticketType, setTicketType] = useState('seated'); // 'seated', 'standing', 'none'
  const [stats, setStats] = useState({
    bookedSeats: 0,
    totalSeats: 0,
    availableSeats: 0,
    standingCapacity: 0,
    standingBooked: 0,
    standingAvailable: 0
  });

  // Show house full alert
  const [showHouseFullAlert, setShowHouseFullAlert] = useState(false);
  
  // No tickets available state
  const [noTicketsAvailable, setNoTicketsAvailable] = useState(false);

  // ===== Function to validate email format =====
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // ===== Handle email input change =====
  const handleEmailChange = (e) => {
    const email = e.target.value;
    setCustomer({...customer, email: email});
  };

  // ===== Function to send booking confirmation email =====
  const sendBookingEmail = async (bookingDetails, customer, event, selectedSeat, ticketQuantity, amount) => {
    let emailMessage = "";
    
    try {
      const ticketType = selectedSeat?.type === 'standing' ? 'Standing' : 'Seated';
      const seatInfo = selectedSeat?.type !== 'standing' ? `Seat #: ${selectedSeat.number}` : '';
      const quantityInfo = selectedSeat?.type === 'standing' ? `Quantity: ${ticketQuantity} tickets` : '';
      const transactionId = bookingDetails?.paymentId || bookingDetails?.bookingId || 'N/A';
      const date = new Date().toLocaleString();
      
      emailMessage = `
        🎫 TICKET BOOKING CONFIRMATION
        ================================
        
        EVENT DETAILS:
        • Event: ${event?.eventName || 'N/A'}
        • Date: ${event?.eventDate || 'N/A'}
        • Venue: ${event?.place || 'N/A'}
        • Ticket Type: ${ticketType}
        ${seatInfo ? `• ${seatInfo}` : ''}
        ${quantityInfo ? `• ${quantityInfo}` : ''}
        
        CUSTOMER DETAILS:
        • Name: ${customer.name}
        • Phone: ${customer.phone}
        • Email: ${customer.email}
        
        PAYMENT DETAILS:
        • Amount Paid: ₹${amount?.toLocaleString() || '0'}
        • Payment Status: SUCCESS ✅
        • Transaction ID: ${transactionId}
        • Booking Time: ${date}
        
        Your Ticket Code: ${bookingDetails?.receiptId || 'TKT-' + Date.now()}
        
        Thank you for choosing MEDIA WAGON! We look forward to seeing you at the event.
        
        For any queries, contact us at support@mediawagon.com
      `;
      
      // Email endpoint - keep as is or update to PHP
      await axios.post('http://localhost:8007/api/email/send-booking-confirmation', {
        email: customer.email,
        name: customer.name,
        phone: customer.phone,
        message: emailMessage
      });
      
      console.log("✅ Booking confirmation email sent successfully");
      
    } catch (error) {
      console.error("❌ Failed to send booking email:", error);
    }
  };

  // Fetch event details and tickets - UPDATED TO PHP API with ticket type support
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setNoTicketsAvailable(false);
        
        if (!eventId) {
          console.error("No event ID provided");
          setLoading(false);
          return;
        }

        console.log("Fetching data for event:", eventId);
        
        // Fetch event from PHP API
        const eventRes = await axios.get(`${API_BASE_URL}/events.php?eventId=${eventId}`);
        console.log("Event details received:", eventRes.data);
        
        const eventData = eventRes.data;
        setEvent(eventData);
        
        // Get ticket type from event (from customLayoutData or direct field)
        let eventTicketType = 'seated';
        if (eventData.ticketType) {
          eventTicketType = eventData.ticketType;
        } else if (eventData.customLayoutData) {
          try {
            const layoutData = typeof eventData.customLayoutData === 'string' 
              ? JSON.parse(eventData.customLayoutData) 
              : eventData.customLayoutData;
            if (layoutData.ticketType) {
              eventTicketType = layoutData.ticketType;
            }
          } catch (e) {
            console.log("Error parsing layout data for ticket type");
          }
        }
        
        setTicketType(eventTicketType);
        console.log("Ticket type:", eventTicketType);
        
        // If ticket type is 'none', show no tickets message
        if (eventTicketType === 'none') {
          setNoTicketsAvailable(true);
          setShowHouseFullAlert(true);
          setLoading(false);
          return;
        }
        
        // Fetch ticket bookings from PHP API
        let allTicketBookings = [];
        try {
          const ticketRes = await axios.get(`${API_BASE_URL}/ticket-bookings.php?eventId=${eventId}`);
          console.log("Ticket bookings from API:", ticketRes.data);
          if (Array.isArray(ticketRes.data)) {
            allTicketBookings = ticketRes.data;
          }
        } catch (ticketError) {
          console.error("Error fetching ticket bookings:", ticketError);
          allTicketBookings = [];
        }
        
        if (eventData.isCustomLayout) {
          console.log("Custom layout detected");
          
          try {
            if (eventTicketType === 'standing') {
              // Standing event
              const standingBookings = allTicketBookings.filter(b => b.ticketType === 'standing');
              const totalBooked = standingBookings.reduce((sum, b) => sum + (b.quantity || 1), 0);
              const standingAvailable = (eventData.standingCapacity || 0) - totalBooked;
              
              setStats({
                totalSeats: 0,
                bookedSeats: 0,
                availableSeats: 0,
                standingCapacity: eventData.standingCapacity || 0,
                standingBooked: totalBooked,
                standingAvailable: standingAvailable
              });
              
              if (standingAvailable <= 0 && eventData.standingCapacity > 0) {
                setNoTicketsAvailable(true);
              }
              setBookedSeats([]);
            } else {
              // Seated event
              const seatedBookings = allTicketBookings.filter(b => b.ticketType === 'seated' || b.seatNo);
              const bookedSeatNumbers = seatedBookings.map(b => b.seatNo).filter(seat => seat);
              
              setBookedSeats(bookedSeatNumbers);
              setStats({
                totalSeats: eventData.totalSeats || 0,
                bookedSeats: bookedSeatNumbers.length,
                availableSeats: (eventData.totalSeats || 0) - bookedSeatNumbers.length,
                standingCapacity: 0,
                standingBooked: 0,
                standingAvailable: 0
              });
              
              if (bookedSeatNumbers.length >= (eventData.totalSeats || 0) && eventData.totalSeats > 0) {
                setNoTicketsAvailable(true);
              }
            }
            setTickets(allTicketBookings);
          } catch (error) {
            console.error("Error processing custom tickets:", error);
            if (eventTicketType === 'standing') {
              setStats({
                totalSeats: 0,
                bookedSeats: 0,
                availableSeats: 0,
                standingCapacity: eventData.standingCapacity || 0,
                standingBooked: 0,
                standingAvailable: eventData.standingCapacity || 0
              });
            } else {
              setStats({
                totalSeats: eventData.totalSeats || 0,
                bookedSeats: 0,
                availableSeats: eventData.totalSeats || 0,
                standingCapacity: 0,
                standingBooked: 0,
                standingAvailable: 0
              });
            }
            setBookedSeats([]);
            setTickets([]);
          }
        } else {
          // Predefined layout event
          try {
            if (eventTicketType === 'seated') {
              const seatedBookings = allTicketBookings.filter(b => b.ticketType === 'seated' || b.seatNo);
              const booked = seatedBookings.map(b => b.seatNo).filter(seat => seat);
              
              setBookedSeats(booked);
              const totalSeats = eventData.totalSeats || (eventData.seatRows * eventData.seatColumns) || 100;
              const bookedSeatsCount = booked.length;
              const availableSeats = totalSeats - bookedSeatsCount;
              
              setStats({
                totalSeats: totalSeats,
                bookedSeats: bookedSeatsCount,
                availableSeats: availableSeats,
                standingCapacity: 0,
                standingBooked: 0,
                standingAvailable: 0
              });
              
              if (availableSeats <= 0) setNoTicketsAvailable(true);
            } else if (eventTicketType === 'standing') {
              const standingBookings = allTicketBookings.filter(b => b.ticketType === 'standing');
              const totalBooked = standingBookings.reduce((sum, b) => sum + (b.quantity || 1), 0);
              const capacity = eventData.standingCapacity || 0;
              const standingAvailable = capacity > 0 ? capacity - totalBooked : Infinity;
              
              setStats({
                totalSeats: 0,
                bookedSeats: 0,
                availableSeats: 0,
                standingCapacity: capacity,
                standingBooked: totalBooked,
                standingAvailable: standingAvailable
              });
              setBookedSeats([]);
              
              if (capacity > 0 && standingAvailable <= 0) setNoTicketsAvailable(true);
            }
            setTickets(allTicketBookings);
          } catch (error) {
            console.log("No tickets found for this event");
            setTickets([]);
            setBookedSeats([]);
            
            if (eventTicketType === 'seated') {
              const totalSeats = eventData.totalSeats || (eventData.seatRows * eventData.seatColumns) || 100;
              setStats({
                totalSeats: totalSeats,
                bookedSeats: 0,
                availableSeats: totalSeats,
                standingCapacity: 0,
                standingBooked: 0,
                standingAvailable: 0
              });
              if (totalSeats <= 0) setNoTicketsAvailable(true);
            } else if (eventTicketType === 'standing') {
              const capacity = eventData.standingCapacity || 0;
              setStats({
                totalSeats: 0,
                bookedSeats: 0,
                availableSeats: 0,
                standingCapacity: capacity,
                standingBooked: 0,
                standingAvailable: capacity > 0 ? capacity : Infinity
              });
              if (capacity <= 0) setNoTicketsAvailable(true);
            }
          }
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    if (eventId) {
      fetchData();
    }
  }, [eventId]);

  // Check if house is full
  useEffect(() => {
    if (ticketType === 'none') {
      setShowHouseFullAlert(true);
      setNoTicketsAvailable(true);
      return;
    }
    
    if (ticketType === 'seated') {
      if (stats.bookedSeats >= stats.totalSeats && stats.totalSeats > 0) {
        setShowHouseFullAlert(true);
        setNoTicketsAvailable(true);
      } else {
        setShowHouseFullAlert(false);
      }
    } else if (ticketType === 'standing') {
      if (stats.standingCapacity > 0 && stats.standingBooked >= stats.standingCapacity) {
        setShowHouseFullAlert(true);
        setNoTicketsAvailable(true);
      } else {
        setShowHouseFullAlert(false);
      }
    }
  }, [stats, event, ticketType]);

  const handleSeatClick = (seatNumber, row, col) => {
    if (ticketType === 'none') {
      alert("This event has no ticket sales.");
      return;
    }
    
    if (noTicketsAvailable || stats.bookedSeats >= stats.totalSeats) {
      alert("No tickets available for this event.");
      return;
    }
    
    if (ticketType !== 'seated') {
      alert("This is not a seated event");
      return;
    }
    
    if (bookedSeats.includes(seatNumber)) {
      alert(`Seat #${seatNumber} is already booked!`);
      return;
    }
    
    setSelectedSeat({ number: seatNumber, row, col });
    setShowPopup(true);
  };

  const handleStandingTicket = () => {
    if (ticketType === 'none') {
      alert("This Event Has No Ticket Sales");
      return;
    }
    
    if (noTicketsAvailable) {
      alert("No tickets available for this event.");
      return;
    }
    
    if (ticketType !== 'standing') {
      alert("This is not a standing event");
      return;
    }
    
    if (!customer.name || !customer.phone) {
      alert("Please fill all required fields");
      return;
    }

    if (customer.phone.length !== 10) {
      alert("Please enter a valid 10-digit phone number");
      return;
    }

    if (!customer.email) {
      alert("Please enter your email address");
      return;
    }

    if (!validateEmail(customer.email)) {
      alert("Please enter a valid email address");
      return;
    }

    if (!ticketQuantity || ticketQuantity < 1) {
      alert("Please enter a valid number of tickets");
      return;
    }

    if (stats.standingCapacity > 0) {
      if (stats.standingBooked + ticketQuantity > stats.standingCapacity) {
        const available = stats.standingCapacity - stats.standingBooked;
        alert(`Cannot book ${ticketQuantity} tickets. Only ${available} ticket(s) available.`);
        return;
      }
    }
    
    // Store booking data before opening payment
    pendingBookingRef.current = {
      type: 'standing',
      quantity: ticketQuantity,
      seat: { type: 'standing', quantity: ticketQuantity },
      customer: { ...customer }
    };
    
    setSelectedSeat({ type: 'standing', quantity: ticketQuantity });
    setShowPaymentPopup(true);
  };

  const incrementQuantity = () => {
    const maxTickets = stats.standingCapacity > 0 
      ? Math.min(20, stats.standingCapacity - stats.standingBooked) 
      : 20;
    
    if (ticketQuantity < maxTickets) {
      setTicketQuantity(prev => prev + 1);
    }
  };

  const decrementQuantity = () => {
    if (ticketQuantity > 1) {
      setTicketQuantity(prev => prev - 1);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setSelectedSeat(null);
    setCustomer({ name: "", phone: "", email: "" });
    setTicketQuantity(1);
    pendingBookingRef.current = null;
  };

  const handleProceedToPayment = () => {
    if (ticketType === 'none') {
      alert("This event has no ticket sales. ");
      return;
    }
    
    if (noTicketsAvailable) {
      alert("No tickets available for this event.");
      return;
    }
    
    if (!customer.name || !customer.phone) {
      alert("Please fill all required fields");
      return;
    }

    if (customer.phone.length !== 10) {
      alert("Please enter a valid 10-digit phone number");
      return;
    }

    if (!customer.email) {
      alert("Please enter your email address");
      return;
    }

    if (!validateEmail(customer.email)) {
      alert("Please enter a valid email address");
      return;
    }

    if (selectedSeat?.type === 'standing' && (!ticketQuantity || ticketQuantity < 1)) {
      alert("Please enter a valid number of tickets");
      return;
    }

    if (selectedSeat?.type !== 'standing' && stats.bookedSeats >= stats.totalSeats) {
      alert("Event is house full! No tickets available.");
      return;
    }

    if (selectedSeat?.type === 'standing' && stats.standingCapacity > 0) {
      if (stats.standingBooked + ticketQuantity > stats.standingCapacity) {
        const available = stats.standingCapacity - stats.standingBooked;
        alert(`Cannot book ${ticketQuantity} tickets. Only ${available} ticket(s) available.`);
        return;
      }
    }
    
    // Store booking data before closing popup
    pendingBookingRef.current = {
      type: 'seated',
      seat: { ...selectedSeat },
      customer: { ...customer },
      ticketQuantity: ticketQuantity
    };
    
    setShowPopup(false);
    setShowPaymentPopup(true);
  };

  // Handle successful payment
  const handlePaymentSuccess = async (paymentData) => {
    console.log("Payment success callback received:", paymentData);
    console.log("Pending booking data:", pendingBookingRef.current);
    
    // Get the stored booking data
    const bookingData = pendingBookingRef.current;
    
    if (!bookingData) {
      console.error("No pending booking data found");
      alert("Booking data not found. Please try again.");
      return;
    }
    
    const ticketPrice = event?.ticketPrice || 1000;
    const amount = paymentData.amount || (bookingData.type === 'standing' 
      ? bookingData.seat?.quantity * ticketPrice 
      : ticketPrice);
    
    try {
      // Prepare booking data for PHP API
      let seatNo = null;
      let quantity = 1;
      let ticketTypeValue = 'seated';
      
      if (bookingData.type === 'standing') {
        ticketTypeValue = 'standing';
        quantity = bookingData.seat?.quantity || ticketQuantity;
        seatNo = null;
      } else {
        seatNo = bookingData.seat?.number;
        quantity = 1;
        ticketTypeValue = 'seated';
      }
      
      const saveBookingData = {
        eventId: parseInt(eventId),
        seatNo: seatNo,
        customerName: bookingData.customer?.name || customer.name,
        customerPhone: bookingData.customer?.phone || customer.phone,
        customerEmail: bookingData.customer?.email || customer.email || "",
        quantity: quantity,
        price: ticketPrice,
        totalPrice: amount,
        ticketType: ticketTypeValue,
        status: 'CONFIRMED'
      };
      
      console.log("Saving booking to PHP API:", saveBookingData);
      
      // Save to PHP API
      const saveResponse = await axios.post(`${API_BASE_URL}/ticket-bookings.php`, saveBookingData);
      console.log("Booking saved:", saveResponse.data);
      
      const bookingDetailsData = {
        ...saveResponse.data,
        eventName: event?.eventName,
        eventDate: event?.eventDate,
        eventPlace: event?.place,
        itemType: bookingData.type === 'standing' ? 'general' : 'seat',
        itemNumber: bookingData.type !== 'standing' ? bookingData.seat?.number : null,
        quantity: bookingData.type === 'standing' ? (bookingData.seat?.quantity || ticketQuantity) : 1,
        customer: {
          name: bookingData.customer?.name || customer.name,
          phone: bookingData.customer?.phone || customer.phone,
          email: bookingData.customer?.email || customer.email || ""
        },
        paymentId: paymentData.paymentUuid,
        orderId: paymentData.orderId,
        receiptId: saveResponse.data.bookingId || "RCPT_" + Date.now(),
        amount: amount,
        bookingTime: new Date().toISOString()
      };
      
      // Update local state
      if (bookingData.type !== 'standing') {
        setBookedSeats(prev => [...prev, bookingData.seat?.number]);
        setStats(prev => ({
          ...prev,
          bookedSeats: prev.bookedSeats + 1,
          availableSeats: prev.availableSeats - 1
        }));
      } else {
        const quantityBooked = bookingData.seat?.quantity || ticketQuantity;
        setStats(prev => ({
          ...prev,
          standingBooked: prev.standingBooked + quantityBooked,
          standingAvailable: prev.standingCapacity > 0 
            ? prev.standingCapacity - (prev.standingBooked + quantityBooked)
            : Infinity
        }));
      }
      
      setBookingDetails(bookingDetailsData);
      
      // Send email confirmation
      await sendBookingEmail(
        bookingDetailsData,
        { name: bookingData.customer?.name || customer.name, 
          phone: bookingData.customer?.phone || customer.phone, 
          email: bookingData.customer?.email || customer.email },
        event,
        bookingData.seat,
        bookingData.type === 'standing' ? (bookingData.seat?.quantity || ticketQuantity) : 1,
        amount
      );
      
      setShowReceipt(true);
      alert(`✅ Payment successful! Ticket(s) booked successfully. A confirmation email has been sent to ${bookingData.customer?.email || customer.email}.`);
      
      // Reset all states
      setCustomer({ name: "", phone: "", email: "" });
      setSelectedSeat(null);
      setTicketQuantity(1);
      pendingBookingRef.current = null;
      
    } catch (error) {
      console.error("Error saving booking:", error);
      alert("Payment was successful but booking failed. Please contact support.");
    }
  };

  const renderSeatGrid = () => {
    if (ticketType !== 'seated') {
      return null;
    }
    
    const rows = event.seatRows || 10;
    const cols = event.seatColumns || 10;
    
    const grid = [];

    for (let row = 0; row < rows; row++) {
      const rowSeats = [];
      for (let col = 0; col < cols; col++) {
        const seatNumber = row * cols + col + 1;
        const isBooked = bookedSeats.includes(seatNumber);
        rowSeats.push(
          <div
            key={seatNumber}
            className={`seat ${isBooked ? 'booked' : 'available'}`}
            onClick={() => !noTicketsAvailable && handleSeatClick(seatNumber, row + 1, col + 1)}
            title={`Seat #${seatNumber} - Row ${String.fromCharCode(65 + row)}`}
          >
            {seatNumber}
          </div>
        );
      }
      grid.push(
        <div key={row} className="seat-row">
          <span className="row-label">Row {String.fromCharCode(65 + row)}</span>
          <div className="seats-grid">
            {rowSeats}
          </div>
        </div>
      );
    }
    return grid;
  };

  const renderMapElements = () => {
    // If no tickets available or ticket type is 'none', show only the message
    if (ticketType === 'none') {
      return (
        <div className="responsive-map-container">
          <div className="no-tickets-message-full">
            <div className="no-tickets-card-full" style={{ 
              background: '#e2e3e5', 
              borderRadius: '12px', 
              padding: '40px', 
              textAlign: 'center',
              margin: '20px auto',
              maxWidth: '500px'
            }}>
              <span className="no-tickets-icon" style={{ fontSize: '48px', display: 'block', marginBottom: '15px' }}>🚫</span>
              <h3 style={{ color: '#383d41', marginBottom: '10px' }}>No Ticket Sales</h3>
              <p style={{ color: '#383d41' }}>This event has no ticket sales.</p>
              <p style={{ color: '#383d41', fontSize: '12px', marginTop: '10px' }}>No booking required. Just show up at the venue.</p>
            </div>
          </div>
        </div>
      );
    }
    
    if (noTicketsAvailable && ticketType !== 'none') {
      return (
        <div className="responsive-map-container">
          <div className="no-tickets-message-full">
            <div className="no-tickets-card-full" style={{ 
              background: '#f8d7da', 
              borderRadius: '12px', 
              padding: '40px', 
              textAlign: 'center',
              margin: '20px auto',
              maxWidth: '500px'
            }}>
              <span className="no-tickets-icon" style={{ fontSize: '48px', display: 'block', marginBottom: '15px' }}>🎫</span>
              <h3 style={{ color: '#721c24', marginBottom: '10px' }}>No Tickets Available</h3>
              <p style={{ color: '#721c24' }}>Sorry, all tickets for this event have been sold out.</p>
              <p style={{ color: '#721c24', fontSize: '12px', marginTop: '10px' }}>Please check back later or contact support for more information.</p>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="responsive-map-container">
        <div className="map-elements-container">
          <div className="map-row top">
            {event?.showStage && (
              <div className="map-box stage-info">
                <span className="map-icon">🎪</span>
                <span className="map-text">STAGE</span>
              </div>
            )}
            {event?.showEntrance && (
              <div className="map-box entrance">
                <span className="map-icon">🚪</span>
                <span className="map-text">ENTRANCE</span>
              </div>
            )}
            {event?.showExit && (
              <div className="map-box exit">
                <span className="map-icon">🚪</span>
                <span className="map-text">EXIT</span>
              </div>
            )}
          </div>

          <div className="map-row middle">
            <div className="map-side left">
              {event?.showFoodCourt && (
                <div className="map-box food-court">
                  <span className="map-icon">🍔</span>
                  <span className="map-text">FOOD COURT</span>
                </div>
              )}
              {event?.showRestrooms && (
                <div className="map-box restrooms">
                  <span className="map-icon">🚻</span>
                  <span className="map-text">RESTROOMS</span>
                </div>
              )}
            </div>

            <div className="seating-wrapper">
              {ticketType === 'seated' ? (
                <div className="seating-section">
                  <div className="seating-area">
                    {renderSeatGrid()}
                  </div>
                </div>
              ) : ticketType === 'standing' ? (
                <div className="standing-section text-center">
                  <div className="standing-card">
                    <div className="standing-icon">🎫</div>
                    <h3>GENERAL ADMISSION</h3>
                    <p className="standing-desc">This is a standing event - no assigned seats</p>
                    
                    <div className="quantity-section">
                      <label className="form-label fw-bold">Number of Tickets</label>
                      <div className="quantity-selector">
                        <button 
                          type="button" 
                          className="quantity-btn minus"
                          onClick={decrementQuantity}
                          disabled={ticketQuantity <= 1 || noTicketsAvailable}
                        >
                          −
                        </button>
                        <input
                          type="number"
                          className="quantity-input"
                          value={ticketQuantity}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            const maxTickets = stats.standingCapacity > 0 
                              ? Math.min(20, stats.standingCapacity - stats.standingBooked) 
                              : 20;
                            if (val >= 1 && val <= maxTickets) {
                              setTicketQuantity(val);
                            }
                          }}
                          min="1"
                          max={stats.standingCapacity > 0 
                            ? Math.min(20, stats.standingCapacity - stats.standingBooked) 
                            : 20}
                          disabled={noTicketsAvailable}
                        />
                        <button 
                          type="button" 
                          className="quantity-btn plus"
                          onClick={incrementQuantity}
                          disabled={
                            noTicketsAvailable ||
                            (stats.standingCapacity > 0 
                              ? ticketQuantity >= stats.standingCapacity - stats.standingBooked || ticketQuantity >= 20
                              : ticketQuantity >= 20)
                          }
                        >
                          +
                        </button>
                      </div>
                      <small className="text-muted">
                        {stats.standingCapacity > 0 
                          ? `${stats.standingAvailable} tickets available out of ${stats.standingCapacity} total` 
                          : 'Unlimited tickets available'}
                      </small>
                    </div>
                    
                    <div className="customer-form">
                      <div className="form-group">
                        <label className="form-label fw-bold">Customer Name *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={customer.name}
                          onChange={(e) => setCustomer({...customer, name: e.target.value})}
                          placeholder="Enter your name"
                          disabled={noTicketsAvailable}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label fw-bold">Phone Number *</label>
                        <input
                          type="tel"
                          className="form-control"
                          value={customer.phone}
                          onChange={(e) => setCustomer({...customer, phone: e.target.value})}
                          placeholder="10-digit mobile number"
                          maxLength="10"
                          disabled={noTicketsAvailable}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label fw-bold">Email Address *</label>
                        <input
                          type="email"
                          className="form-control"
                          value={customer.email}
                          onChange={handleEmailChange}
                          placeholder="your@email.com"
                          disabled={noTicketsAvailable}
                        />
                        {customer.email && !validateEmail(customer.email) && (
                          <small className="text-danger">Please enter a valid email address</small>
                        )}
                      </div>
                      
                      <div className="total-price-section">
                        <span className="total-label">Total Amount:</span>
                        <span className="total-amount">₹{(ticketQuantity * (event?.ticketPrice || 1000)).toLocaleString()}</span>
                      </div>
                      
                      <button 
                        className="btn-buy-tickets"
                        onClick={handleStandingTicket}
                        disabled={noTicketsAvailable || !customer.name || !customer.phone || !customer.email || !validateEmail(customer.email)}
                      >
                        Buy Tickets
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="map-side right">
              {event?.showParking && (
                <div className="map-box parking">
                  <span className="map-icon">🅿️</span>
                  <span className="map-text">PARKING</span>
                </div>
              )}
              {event?.showFirstAid && (
                <div className="map-box first-aid">
                  <span className="map-icon">🏥</span>
                  <span className="map-text">FIRST AID</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Get item details for payment popup
  const getItemDetails = () => {
    const ticketPrice = event?.ticketPrice || 1000;
    const amount = selectedSeat?.type === 'standing' 
      ? ticketQuantity * ticketPrice 
      : ticketPrice;
    
    if (selectedSeat?.type === 'standing') {
      return {
        name: `${ticketQuantity} General Admission Ticket(s)`,
        type: 'general'
      };
    } else if (selectedSeat) {
      return {
        name: `Seat #${selectedSeat.number}`,
        type: 'seat'
      };
    }
    return { name: 'Ticket Booking', type: 'ticket' };
  };

  // Get amount for payment popup
  const getPaymentAmount = () => {
    const ticketPrice = event?.ticketPrice || 1000;
    return selectedSeat?.type === 'standing' 
      ? ticketQuantity * ticketPrice 
      : ticketPrice;
  };

  if (loading) {
    return <div className="loading">Loading event layout...</div>;
  }

  if (!event) {
    return <div className="loading">Event not found</div>;
  }

  return (
    <div className="ticket-booking-page">
      <Navbar />
      
      <div className="event-container">
        {showHouseFullAlert && ticketType !== 'none' && !noTicketsAvailable && (
          <div className="house-full-alert">
            <div className="alert-content">
              <span className="alert-icon">🏠</span>
              <div className="alert-text">
                <h4>Event is House Full!</h4>
                <p>No tickets are available for this event.</p>
              </div>
              <button className="alert-close" onClick={() => setShowHouseFullAlert(false)}>×</button>
            </div>
          </div>
        )}

        <div className="header">
          <h2>{event?.eventName || 'EVENT ARENA'}</h2>
          <div className="stats" style={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <div style={{
              background: '#f8f9fa',
              padding: '6px 12px',
              borderRadius: '8px',
              textAlign: 'center',
              minWidth: '65px'
            }}>
              <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: '500' }}>Type</div>
              <div style={{ fontSize: '14px', fontWeight: 'bold', color: ticketType === 'none' ? '#6c757d' : (ticketType === 'seated' ? '#28a745' : '#ffc107') }}>
                {ticketType === 'none' ? 'No Ticket Sale' : (ticketType === 'seated' ? 'Seat' : 'Stand')}
              </div>
            </div>
            
            {ticketType === 'seated' && !noTicketsAvailable && (
              <>
                <div style={{
                  background: '#f8f9fa',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  minWidth: '65px'
                }}>
                  <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: '500' }}>Total</div>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#007bff' }}>{stats.totalSeats}</div>
                </div>
                <div style={{
                  background: '#f8f9fa',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  minWidth: '65px'
                }}>
                  <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: '500' }}>Booked</div>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#dc3545' }}>{stats.bookedSeats}</div>
                </div>
                <div style={{
                  background: '#f8f9fa',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  minWidth: '65px'
                }}>
                  <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: '500' }}>Avail</div>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#28a745' }}>{stats.availableSeats}</div>
                </div>
              </>
            )}
            
            {ticketType === 'seated' && noTicketsAvailable && (
              <div style={{
                background: '#dc3545',
                padding: '6px 12px',
                borderRadius: '8px',
                textAlign: 'center',
                minWidth: '100px'
              }}>
                <div style={{ fontSize: '11px', color: '#fff', fontWeight: '500' }}>Status</div>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#fff' }}>SOLD OUT</div>
              </div>
            )}
            
            {ticketType === 'standing' && !noTicketsAvailable && (
              <>
                <div style={{
                  background: '#f8f9fa',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  minWidth: '65px'
                }}>
                  <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: '500' }}>Capacity</div>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#007bff' }}>
                    {stats.standingCapacity > 0 ? stats.standingCapacity : '∞'}
                  </div>
                </div>
                <div style={{
                  background: '#f8f9fa',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  minWidth: '65px'
                }}>
                  <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: '500' }}>Booked</div>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#dc3545' }}>{stats.standingBooked}</div>
                </div>
                <div style={{
                  background: '#f8f9fa',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  minWidth: '65px'
                }}>
                  <div style={{ fontSize: '11px', color: '#6c757d', fontWeight: '500' }}>Avail</div>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#28a745' }}>
                    {stats.standingCapacity > 0 ? stats.standingAvailable : '∞'}
                  </div>
                </div>
              </>
            )}
            
            {ticketType === 'standing' && noTicketsAvailable && (
              <div style={{
                background: '#dc3545',
                padding: '6px 12px',
                borderRadius: '8px',
                textAlign: 'center',
                minWidth: '100px'
              }}>
                <div style={{ fontSize: '11px', color: '#fff', fontWeight: '500' }}>Status</div>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#fff' }}>SOLD OUT</div>
              </div>
            )}
            
            {ticketType === 'none' && (
              <div style={{
                background: '#6c757d',
                padding: '6px 12px',
                borderRadius: '8px',
                textAlign: 'center',
                minWidth: '100px'
              }}>
                <div style={{ fontSize: '11px', color: '#fff', fontWeight: '500' }}>Status</div>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#fff' }}>NO TICKETS</div>
              </div>
            )}
          </div>
        </div>

        <div className="main-layout">
          {renderMapElements()}
        </div>

        <div className="terms-section">
          <div className="terms-container">
            <div className="term-item">
              <span className="term-icon">⚠️</span>
              <span className="term-text">We are not responsible for your belongings</span>
            </div>
            <div className="term-item">
              <span className="term-icon">🚫</span>
              <span className="term-text">Tickets are not cancellable</span>
            </div>
            <div className="term-item">
              <span className="term-icon">💰</span>
              <span className="term-text">No refund for tickets</span>
            </div>
          </div>
        </div>

        {/* Booking Popup - Only for seated events */}
        {showPopup && selectedSeat && selectedSeat?.type !== 'standing' && ticketType === 'seated' && (
          <div className="popup-overlay" onClick={closePopup}>
            <div className="popup-container" onClick={(e) => e.stopPropagation()}>
              <button className="popup-close" onClick={closePopup}>×</button>
              <h5 className="mb-3">Book Seat #{selectedSeat.number}</h5>
              <p>Price: ₹{event?.ticketPrice || 1000}</p>
              <p className="text-muted">Row: {String.fromCharCode(65 + selectedSeat.row - 1)}</p>
              
              <input
                type="text"
                className="form-control mb-2"
                placeholder="Customer Name *"
                value={customer.name}
                onChange={e => setCustomer({...customer, name: e.target.value})}
              />
              
              <input
                type="tel"
                className="form-control mb-2"
                placeholder="Phone Number *"
                value={customer.phone}
                onChange={e => setCustomer({...customer, phone: e.target.value})}
                maxLength="10"
              />
              
              <div className="mb-3">
                <label className="form-label">Email Address *</label>
                <input
                  type="email"
                  className="form-control"
                  placeholder="your@email.com"
                  value={customer.email}
                  onChange={handleEmailChange}
                />
                {customer.email && !validateEmail(customer.email) && (
                  <small className="text-danger">Please enter a valid email address</small>
                )}
              </div>
              
              <div className="d-flex justify-content-between">
                <button 
                  className="btn btn-secondary"
                  onClick={closePopup}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-success"
                  onClick={handleProceedToPayment}
                  disabled={!customer.name || !customer.phone || !customer.email || !validateEmail(customer.email)}
                >
                  Proceed to Pay
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Popup - Only for seated/standing events, not for 'none' */}
        {ticketType !== 'none' && (
          <PaymentPopup
            isOpen={showPaymentPopup}
            onClose={() => {
              setShowPaymentPopup(false);
              pendingBookingRef.current = null;
              if (selectedSeat?.type === 'standing') {
                setSelectedSeat(null);
              } else {
                setShowPopup(true);
              }
            }}
            amount={getPaymentAmount()}
            itemDetails={getItemDetails()}
            onPaymentSuccess={handlePaymentSuccess}
          />
        )}

        {/* Payment Receipt Popup */}
        <PaymentReceipt
          isOpen={showReceipt}
          onClose={() => setShowReceipt(false)}
          bookingDetails={bookingDetails}
        />

        <Footer />
      </div>
    </div>
  );
}

export default Ticket_Booking;